<template>
  <v-layout wrap>
    <v-flex xs12>
      <v-card>
        <Subheader v-model="search" search="aliases" title="Aliases">
          <v-btn color="success" dark @click="clickAddAlias()">
            <v-icon class="mr-1">add</v-icon>{{ $t('Button.Add_Alias') }}
          </v-btn>
        </Subheader>
        <v-data-table :headers="aliasHeaders" :items="analyteData.Aliases" :pagination.sync="pagination" :search="search">
          <template slot="items" slot-scope="props">
            <td class="text-xs-left">{{ props.item.AliasName }}</td>
            <td class="text-xs-left">{{ props.item.Method }}</td>
            <td class="text-xs-right">
              <v-flex>
                <v-btn @click="clickSetAsNewAnalyte(props.item)">
                  <v-icon color="success" class="mr-1">call_split</v-icon>
                  <span class="text-none">{{ $t('Button.Set_As_Analyte') }}</span>
                </v-btn>
                <v-btn small @click="clickEdit(props.item)">
                  <v-icon class="mr-1" color="warning">edit</v-icon>{{ $t('Button.Edit') }}
                </v-btn>
                <v-btn small @click="clickDeleteAlias(props.item)">
                  <v-icon class="mr-1" color="error">delete_forever</v-icon>{{ $t('Button.Remove') }}
                </v-btn>
              </v-flex>
            </td>
          </template>
        </v-data-table>
      </v-card>
    </v-flex>

    <AppModal v-model="addDialog" persistent max-width="800">
      <template v-slot:title>{{ $t('AnalyteAliaManagement.addTitleMsg') }}</template>
      <template v-slot:text>
        <v-radio-group label="" v-model="addAliasType">
          <v-radio label="Create a new analyte alias" :value="true"></v-radio>
          <v-radio label="Demote another existing analyte to an alias" :value="false"></v-radio>
        </v-radio-group>
        <v-autocomplete v-if="!addAliasType" label="Existing analyte:" v-model="mergeAnalyte" :items="MasterAnalyteList" item-text="AnalyteName" return-object></v-autocomplete>
        <template v-if="addAliasType">
          <v-text-field label="Alias name" v-model="aliasData.AliasName"></v-text-field>
          <v-text-field label="Method" v-model="aliasData.Method"></v-text-field>
        </template>
      </template>
      <template v-slot:actions>
        <v-spacer />
        <v-btn color="add" v-if="!addAliasType" dark @click="addAliasFromAnalyte()">
          <v-icon class="mr-1">done</v-icon>{{ $t('Button.Save') }}
        </v-btn>
        <v-btn color="add" v-else dark @click="addAlias()">
          <v-icon class="mr-1">done</v-icon>{{ $t('Button.Save') }}
        </v-btn>
        <v-btn @click="closeDialog()">{{ $t('Button.Cancel') }}</v-btn>
      </template>
    </AppModal>

    <AppModal v-model="editDialog" persistent max-width="800">
      <template v-slot:title>{{ $t('AnalyteAliaManagement.editTitleMsg') }}</template>
      <template v-slot:text>
        <v-text-field label="Name:" v-model="editAliases.AliasName"></v-text-field>
        <v-text-field label="Method:" v-model="editAliases.Method"></v-text-field>
      </template>
      <template v-slot:actions>
        <v-spacer />
        <v-btn color="add" dark @click="editAlias()">
          <v-icon class="mr-1">done</v-icon>{{ $t('Button.Save') }}
        </v-btn>
        <v-btn @click="closeDialog()">{{ $t('Button.Cancel') }}</v-btn>
      </template>
    </AppModal>

    <AppModal v-model="configDialog" persistent max-width="800">
      <template v-slot:title>{{ $t('Button.Delete_Alias') }}</template>
      <template v-slot:text>
        <v-card-text>{{ $t('AnalyteAliaManagement.deleteTitleMsg', { msg: deleteAliasData.AliasName }) }}</v-card-text>
      </template>
      <template v-slot:actions>
        <v-spacer></v-spacer>
        <v-btn color="delete" @click.native="deleteAlias()" dark>
          <v-icon class="mr-1">delete_forever</v-icon>{{ $t('Button.REMOVE_ALIAS') }}
        </v-btn>
        <v-btn @click.native="closeDialog()">{{ $t('Button.CANCEL') }}</v-btn>
      </template>
    </AppModal>

    <AppModal v-model="setAsNewAnalyteDialog" persistent max-width="800">
      <template v-slot:title>{{ $t('Button.Set_As_Analyte') }}</template>
      <template v-slot:text>
        <v-card-text>{{ $t('AnalyteAliaManagement.setTitleMsg', { analyteName: newAnalyteAlias.AliasName }) }}</v-card-text>
      </template>
      <template v-slot:actions>
        <v-spacer></v-spacer>
        <v-btn color="add" dark @click.native="SetAsNewAnalyte()">
          <v-icon class="mr-1">done</v-icon>{{ $t('Button.SET_AS_ANALYTE') }}
        </v-btn>
        <v-btn @click.native="closeDialog()">{{ $t('Button.CANCEL') }}</v-btn>
      </template>
    </AppModal>

    <v-card>
      <v-snackbar :color="snackColor" :timeout="null" top v-model="snackbar" auto-height>
        {{ snackMessage }}
        <v-btn dark flat @click.native="snackbar = false">{{ $t('Button.Close') }}</v-btn>
      </v-snackbar>
    </v-card>

    <ErrorDialog v-model="errorDialog.show" :data="errorDialog.data" :title="errorDialog.title"></ErrorDialog>

  </v-layout>
</template>

<script>
  import {
    createNamespacedHelpers,
  } from 'vuex'
  import ErrorDialog from '@/components/generic/ErrorDialog'
  import Subheader from '@/components/generic/Subheader'
  import AppModal from '@/components/generic/AppModal'
  const analyte = createNamespacedHelpers('analyte')
  const authenticate = createNamespacedHelpers('authenticate')

  export default {
    components: {
      ErrorDialog,
      Subheader,
      AppModal,
    },
    props: {
      analyteData: {
        type: Object,
        default: () => {},
      },
    },
    data() {
      return {
        search: '',
        addDialog: false,
        configDialog: false,
        editDialog: false,
        setAsNewAnalyteDialog: false,
        auditDialog: false,
        snackColor: '',
        snackMessage: '',
        snackbar: false,
        nowEditAliasName: '',
        aliasHeaders: [{
          text: 'Alias Name',
          value: 'AliasName',
          align: 'left',
          sortable: false,
        },
        {
          text: 'Method',
          value: 'Method',
          align: 'left',
        },
        {
          text: 'Action',
          value: '',
          align: 'right',
        },
        ],
        headers: [{
          text: 'Alias Name',
          value: 'state',
          align: 'left',
          sortable: false,
        },
        {
          text: 'Datetime',
          value: 'datetime',
          align: 'left',
        },
        {
          text: 'Notes',
          value: 'notes',
          align: 'left',
        },
        {
          text: 'Approved By',
          value: 'approvedBy',
          align: 'left',
        },
        ],
        pagination: {
          sortBy: 'AliasName',
          rowsPerPage: 10,
        },
        auditAlias: {},
        addAliasType: true,
        mergeAnalyte: {},
        aliasData: {},
        deleteAliasData: {},
        editAliases: {
          AliasName: '',
          AnalyteId: '',
          Id: '',
          IsPrimary: '',
          Method: '',
        },
        newAnalyteAlias: {},
        auditNote: '',
        TenantList: [{
          TenantName: 'JDS',
          MainAlia: 'Alkalinity (123)',
        },
        {
          TenantName: 'Admin',
          MainAlia: 'Alkalinity (Total as CaCO3)',
        },
        ],
      }
    },
    mounted() {},
    computed: {
      ...analyte.mapGetters(['tenantAnalyteList']),
      ...authenticate.mapGetters(['currentUserGroup']),
      MasterAnalyteList() {
        let arr = JSON.parse(JSON.stringify(this.tenantAnalyteList))
        let i = arr.findIndex(x => x.Id == this.analyteData.Id)
        if (i >= 0) {
          arr.splice(i, 1)
        }
        return arr
      },
      errorDialog() {
        return this.$store.getters['app/errorDialog']
      },
    },
    watch: {},
    methods: {
      ...analyte.mapActions(['SetPrimaryAlias', 'addTenantAnalyteAsAlias', 'editTenantAnalyteAsAlias', 'deleteTenantAnalyteAsAlias', 'setAliasAsNewAnalyte', 'setTenantAnalyteAsAlias']),
      SetAsPrimary(data) {
        this.SetPrimaryAlias(data)
      },
      SetAsNewAnalyte() {
        let options = {
          data: {
            Id: this.newAnalyteAlias.Id,
            AnalyteId: this.newAnalyteAlias.AnalyteId,
            AliasName: this.newAnalyteAlias.AliasName,
            Method: this.newAnalyteAlias.Method,
            TenantId: this.currentUserGroup.id,
            DefaultUnitId: this.analyteData.DefaultUnitId,
            ApplicableUnitGroupId: this.analyteData.ApplicableUnitGroupId,
          },
          successCb: () => {
            this.enableSnackbar('success', this.$t('AnalyteAliaManagement.successSaveMsg'))
            this.setAsNewAnalyteDialog = false
          },
          failedCb: err => {
            this.$store.dispatch('app/setErrorDialog', {
              data: err,
              title: err.message,
              show: true,
            })
          },
        }
        this.setAliasAsNewAnalyte(options)
      },
      addAliasFromAnalyte() {
        if (Object.keys(this.mergeAnalyte).length != 0 && this.mergeAnalyte.constructor === Object) {
          let options = {
            data: {
              sourceAnalyte: this.mergeAnalyte,
              targetAnalyte: this.analyteData,
            },
            successCb: () => {
              this.enableSnackbar('success', this.$t('AnalyteDetailInformation.successSaveMsg'))
              this.mergeAnalyte = {}
              this.addDialog = false
            },
            failedCb: (err) => {
              this.$store.dispatch('app/setErrorDialog', {
                data: err.response.data.ExceptionMessage,
                title: '',
                show: true,
              })
            },
          }
          this.setTenantAnalyteAsAlias(options)
        } else {
          this.enableSnackbar('info', this.$t('AnalyteDetailInformation.nullLinkMsg'))
        }
      },
      auditAliasDialog(data) {
        this.auditAlias = data
        this.auditDialog = !this.auditDialog
      },
      clickAddAlias() {
        this.addDialog = true
      },
      closeDialog() {
        this.configDialog = false
        this.addDialog = false
        this.setAsNewAnalyteDialog = false
        this.editDialog = false
      },
      addAlias() {
        let repeat = false
        if (this.aliasData.AliasName && this.aliasData.Method) {
          let i = this.analyteData.Aliases.findIndex(a => a.AliasName == this.aliasData.AliasName && a.Method == this
            .aliasData.Method)
          if (i >= 0) {
            repeat = true
          }
          if (repeat) {
            this.enableSnackbar('info', this.$t('AnalyteAliaManagement.repeatMsg', {
              msg: this.aliasData.AliasName,
            }))
          } else {
            let options = {
              data: {
                Id: null,
                AliasName: this.aliasData.AliasName,
                Method: this.aliasData.Method,
                AnalyteId: this.analyteData.Id,
                TenantId: this.currentUserGroup.id,
              },
              successCb: () => {
                this.enableSnackbar('success', this.$t('AnalyteAliaManagement.successSaveMsg'))
                this.aliasData = {}
                this.addDialog = false
              },
              failedCb: err => {
                this.$store.dispatch('app/setErrorDialog', {
                  data: err,
                  title: err.message,
                  show: true,
                })
              },
            }
            this.addTenantAnalyteAsAlias(options)
          }
        } else {
          this.enableSnackbar('error', this.$t('AnalyteAliaManagement.nullMsg'))
        }
      },
      editAlias() {
        let arr = JSON.parse(JSON.stringify(this.analyteData.Aliases))
        let target = arr.findIndex(x => x.Id == this.editAliases.Id)
        if (target > -1) {
          arr.splice(target, 1)
        }
        let repeat = false
        if (this.editAliases.AliasName && this.editAliases.Method) {
          let i = arr.findIndex(a => a.AliasName == this.editAliases.AliasName && a.Method == this
            .editAliases.Method)
          if (i >= 0) {
            repeat = true
          }
          if (repeat) {
            this.enableSnackbar('info', this.$t('AnalyteAliaManagement.repeatMsg', {
              msg: this.editAliases.AliasName,
            }))
          } else {
            let options = {
              data: {
                Id: this.editAliases.Id,
                AliasName: this.editAliases.AliasName,
                Method: this.editAliases.Method,
                AnalyteId: this.editAliases.AnalyteId,
                TenantId: this.currentUserGroup.id,
              },
              successCb: () => {
                this.editDialog = false
                this.enableSnackbar('success', this.$t('AnalyteAliaManagement.successSaveMsg'))
              },
              failedCb: err => {
                this.$store.dispatch('app/setErrorDialog', {
                  data: err,
                  title: err.message,
                  show: true,
                })
              },
            }
            this.editTenantAnalyteAsAlias(options)
          }
        } else {
          this.enableSnackbar('error', this.$t('AnalyteAliaManagement.nullMsg'))
        }
      },
      clickDeleteAlias(data) {
        this.deleteAliasData = data
        this.configDialog = true
      },
      clickSetAsNewAnalyte(data) {
        this.newAnalyteAlias = data
        this.setAsNewAnalyteDialog = true
      },
      deleteAlias() {
        let options = {
          data: this.deleteAliasData,
          successCb: () => {
            this.enableSnackbar('success', this.$t('AnalyteAliaManagement.successDeleteMsg'))
            this.configDialog = false
          },
          failedCb: err => {
            this.$store.dispatch('app/setErrorDialog', {
              data: err,
              title: err.message,
              show: true,
            })
          },
        }
        this.deleteTenantAnalyteAsAlias(options)
      },
      clickEdit(data) {
        this.editDialog = true
        this.emptyeditAliases()
        this.editAliases.AliasName = data.AliasName
        this.nowEditAliasName = data.AliasName
        this.editAliases.AnalyteId = data.AnalyteId
        this.editAliases.Id = data.Id
        this.editAliases.IsPrimary = data.IsPrimary
        this.editAliases.Method = data.Method
      },
      emptyeditAliases() {
        this.editAliases = {
          AliasName: '',
          AnalyteId: '',
          Id: '',
          IsPrimary: '',
          Method: '',
        }
      },
      enableSnackbar(color, message) {
        this.snackbar = true
        this.snackColor = color
        this.snackMessage = message
      },
    },
  }
</script>

<style lang="stylus" scoped>
  .toolbar__title.display-2 {
    padding-bottom: 10px;
    margin-left: 10px !important;
  }
</style>